import Cookies from "js-cookie";
import ELocalStorageKeys from "types/LocalStorageKey";

/**
 * Session helpers shared by every HTTP client (RTK Query, axiosInstance,
 * fetchUtil, verifyToken).
 *
 * The store registers its handlers in `store/index.ts`. This module never
 * imports the store, to avoid a circular import
 * (store -> slices -> api hooks -> http client -> store).
 */
type AuthSessionHandlers = {
  onSessionExpired: () => void;
  onTokenRefreshed: (accessToken: string) => void;
};

let handlers: AuthSessionHandlers | null = null;

export const setAuthSessionHandlers = (next: AuthSessionHandlers) => {
  handlers = next;
};

export const notifySessionExpired = () => {
  handlers?.onSessionExpired();
};

// Shared in-flight refresh so parallel 401s trigger only one refresh call.
let refreshPromise: Promise<string | null> | null = null;

const requestNewAccessToken = async (): Promise<string | null> => {
  const refreshToken = Cookies.get(ELocalStorageKeys.RefreshToken);

  if (!refreshToken) return null;

  try {
    // No Authorization header: the current access token is expired.
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}api/v2/users/refresh_token`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
      }
    );

    if (!response.ok) return null;

    const data = await response.json();
    const accessToken: string | undefined = data?.body?.access_token;

    if (!accessToken) return null;

    Cookies.set(ELocalStorageKeys.AccessToken, accessToken, {
      secure: true,
      sameSite: "strict",
    });
    handlers?.onTokenRefreshed(accessToken);

    return accessToken;
  } catch {
    return null;
  }
};

/**
 * Exchanges the refresh token for a new access token.
 * Resolves to the new access token, or `null` if the refresh failed
 * (the caller should then end the session).
 */
export const refreshAccessToken = (): Promise<string | null> => {
  if (!refreshPromise) {
    refreshPromise = requestNewAccessToken().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};
